import React, { useState, useRef, type ChangeEvent } from 'react';
import { type AlertColor } from '@mui/material';
import styled from 'styled-components';
import AddIcon from '@mui/icons-material/Add';
import { FileBox } from './FileBox';
import { type FileData, FILE_MANAGE_ORDER } from './types';
import { COMMON_FILE_TEXT, FILE_SIZE_LIMITS } from './constants';
import { Alert, type AlertDetails, Popover } from '../';
import { FILES_PANEL_TEXT } from './lang';
import { postRequest, type PostRequestData } from '../../utils';
import { useAuth } from '../../contexts';

type FilesPanelData = {
  title?: string;
  caption: React.ReactNode;
  files: FileData[];
  maxFileNumber?: number;
  disable?: boolean;
  disableDeleteOnly?: boolean;
  handleFilesChange: (order: number, id: string) => boolean;
  handleLoading?: (callback: () => Promise<void>) => Promise<void>;
  popoverContent?: string;
};

export const FilesPanel: React.FunctionComponent<FilesPanelData> = (
  props: FilesPanelData,
) => {
  const {
    title,
    caption,
    files,
    maxFileNumber = 10,
    disable: disableMode = false,
    disableDeleteOnly = false,
    popoverContent,
    handleFilesChange,
  } = props;
  const { handleLoading } = useAuth();

  const overNumber = files.length === maxFileNumber;
  const disable = overNumber || disableMode;

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFile = async (
    e: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (e.target.files === null || e.target.files.length < 1) return;

    const file = e.target.files[0];
    if (file.type !== 'application/pdf') {
      handleDisplayAlert('warning', 'Only PDF format is accepted.');

      return;
    }
    if (
      file.size < FILE_SIZE_LIMITS.MIN_SIZE ||
      file.size > FILE_SIZE_LIMITS.MAX_SIZE
    ) {
      handleDisplayAlert('warning', COMMON_FILE_TEXT.SIZE_LIMIT);

      return;
    }

    await handleLoading(async () => {
      try {
        const formData = new FormData();
        formData.append('files', file);
        const postRequestData: PostRequestData = {
          url: '/files/upload',
          data: formData,
          responseType: 'json',
        };

        const res = await postRequest(postRequestData);
        const id = res.data.urls[0];
        const newFile = { id, name: file.name, link: '' };
        handleFilesChange(FILE_MANAGE_ORDER.UPLOAD, newFile.id);
        handleDisplayAlert('success', 'Uploaded the File Successfully.');
      } catch (error: any) {
        const errorMsg =
          'Uploaded the File Failed.' + error?.response?.data?.message;
        handleDisplayAlert('error', errorMsg);

        // should reset the ref
        // otherwise once error occurs, next onchange would never trigger the uploadFile function
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    });

    setOpenAlert(true);
  };

  const removeFile = (fileId: string, index: number): void => {
    // Mod 2024/9/16, not delete from server any more.
    // const res = await axios.delete(`/files/${fileId}`);

    const removeFile = files[index];
    handleFilesChange(FILE_MANAGE_ORDER.REMOVE, removeFile.id);
    handleDisplayAlert('success', 'Removed the File Successfully.');
  };

  const handleDisplayAlert = (
    alertType: AlertColor,
    alertContent: string,
  ): void => {
    setOpenAlert(true);
    setAlertDetails({
      alertType,
      alertContent,
    });
  };
  const handleMouseEnterUpload = (e: React.MouseEvent<HTMLElement>): void => {
    if (disable) return;
    if (popoverContent !== undefined) setAnchorEl(e.currentTarget);
  };

  const handleMouseLeaveUpload = (): void => {
    if (disable) return;
    if (popoverContent !== undefined) setAnchorEl(null);
  };

  const handleSetFileInput = (): void => {
    if (disable) return;
    fileInputRef.current?.click();
  };

  const uploadInputkey = files.length; // prevent same file upload causing render problem

  return (
    <Container>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {Boolean(title) && (
        <InfoBox $layout="center">
          {title}
          {FILES_PANEL_TEXT.UPLOAD_NOTES.map((note, index) => {
            return (
              <div
                key={index}
                style={{
                  width: '100%',
                  fontStyle: 'italic',
                  textAlign: 'left',
                  fontSize: '0.8rem',
                }}
              >
                {note}
              </div>
            );
          })}
        </InfoBox>
      )}
      <InfoBox $layout="center">{caption}</InfoBox>
      <InfoBox $bottom={true} $enableAddFile={!disable}>
        {files.map((file, index) => {
          return (
            <FileBox
              key={index}
              file={file}
              index={index}
              fileExtension="pdf"
              disabled={disableMode || disableDeleteOnly} // disable deletion
              removeFile={removeFile}
              handleDisplayAlert={handleDisplayAlert}
              handleLoading={handleLoading}
            />
          );
        })}

        <div
          className="upload-file"
          onMouseEnter={handleMouseEnterUpload}
          onMouseLeave={handleMouseLeaveUpload}
          onClick={handleSetFileInput}
        >
          <input
            key={uploadInputkey}
            ref={fileInputRef}
            type="file"
            onChange={uploadFile}
            accept=".pdf"
            disabled={disable}
          />
          <AddIcon />
        </div>
      </InfoBox>
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        content={popoverContent ?? ''}
        onClose={handleMouseLeaveUpload}
        disableRestoreFocus
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBox = styled.div<{
  $bottom?: boolean;
  $layout?: string;
  $enableAddFile?: boolean;
}>`
  width: 18rem;
  border: 1px solid #e5e5e5;
  border-bottom: ${props => (props?.$bottom ? '1px solid #E5E5E5' : 'none')};
  padding: 0.5rem;
  display: flex;
  flex-direction: row;
  justify-content: ${props => props.$layout};
  align-items: flex-start;
  flex-wrap: wrap;

  .upload-file {
    width: 5rem;
    height: 5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 0.5rem;
    border: 1px dashed #e5e5e5;
    margin: 0.5rem 0.2rem;
    cursor: ${props => (props.$enableAddFile ? 'pointer' : 'not-allowed')};

    input {
      display: none;
    }
  }
`;

FilesPanel.displayName = 'FilesPanel';
